import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiPainel from "../services/apiPainel";
import { conectarSocketPainel } from "../services/socketPainel";

export default function PainelDisputa() {
  const navigate = useNavigate();

  const [operador, setOperador] = useState(null);
  const [disputa, setDisputa] = useState(null);
  const [loading, setLoading] = useState(false);

  const [qtdPremios, setQtdPremios] = useState(5);

  // BOI TV
  const [boiTvNumeroSenha, setBoiTvNumeroSenha] = useState("");
  const [boiTvRodada, setBoiTvRodada] = useState(1);
  const [boiTvNovoResultado, setBoiTvNovoResultado] = useState("VALIDO");
  const [boiTvJuizNome, setBoiTvJuizNome] = useState("");

  // Classificar faixa (ex: 3º ao 5º)
  const [faixaRodada, setFaixaRodada] = useState(1);
  const [faixaDe, setFaixaDe] = useState(3);
  const [faixaAte, setFaixaAte] = useState(5);

  const eventoId = operador?.eventoId;
  const categoria = operador?.categoria;

  const podeJulgar = useMemo(() => operador?.funcao === "JUIZ", [operador]);

  useEffect(() => {
    const op = JSON.parse(localStorage.getItem("painelOperador") || "null");
    if (!op) {
      navigate("/painel");
      return;
    }
    setOperador(op);

    if (op?.nome) setBoiTvJuizNome(op.nome);
  }, [navigate]);

  async function carregarDisputa() {
    if (!eventoId || !categoria) return;
    try {
      const { data } = await apiPainel.get(`/disputa/estado/${eventoId}/${categoria}`);
      setDisputa(data?.disputa || null);
    } catch (e) {
      console.log("[DISPUTA] ainda não existe", e?.response?.data || e.message);
      setDisputa(null);
    }
  }

  useEffect(() => {
    if (!eventoId || !categoria) return;

    carregarDisputa();

    const socket = conectarSocketPainel();
    const channel = `disputa:${eventoId}:${categoria}:update`;

    socket.on("connect", () => console.log("[SOCKET] conectado disputa"));
    socket.on(channel, () => {
      console.log("[SOCKET] update disputa");
      carregarDisputa();
    });

    return () => {
      socket.off(channel);
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId, categoria]);

  async function iniciarDisputa() {
    if (!podeJulgar) return alert("Somente o JUIZ pode iniciar a disputa.");

    setLoading(true);
    try {
      const { data } = await apiPainel.post("/disputa/iniciar", {
        eventoId,
        categoria,
        qtdPremios: Number(qtdPremios),
        operador: { nome: operador.nome, funcao: operador.funcao },
      });

      setDisputa(data?.disputa || null);
    } catch (e) {
      alert(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }

  async function acaoDisputa(acao) {
    if (!podeJulgar) return alert("Somente o JUIZ pode julgar na disputa.");
    if (!disputa) return alert("A disputa ainda não foi iniciada.");

    setLoading(true);
    try {
      const { data } = await apiPainel.post("/disputa/acao", {
        eventoId,
        categoria,
        acao,
        operador: { nome: operador.nome, funcao: operador.funcao },
      });

      setDisputa(data?.disputa || null);
    } catch (e) {
      alert(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }

  async function mudarRodada(rodada) {
    if (!podeJulgar) return alert("Somente o JUIZ pode mudar rodada.");

    setLoading(true);
    try {
      const { data } = await apiPainel.post("/disputa/proxima-rodada", {
        eventoId,
        categoria,
        rodada: Number(rodada),
        operador: { nome: operador.nome, funcao: operador.funcao },
      });

      setDisputa(data?.disputa || null);
    } catch (e) {
      alert(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }

  async function revisaoBoiTv() {
    if (!podeJulgar) return alert("Somente o JUIZ pode usar BOI TV.");
    if (!boiTvNumeroSenha) return alert("Digite o número da senha para BOI TV");

    setLoading(true);
    try {
      const { data } = await apiPainel.post("/disputa/revisao-boitv", {
        eventoId,
        categoria,
        rodada: Number(boiTvRodada),
        numeroSenha: Number(boiTvNumeroSenha),
        novoResultado: boiTvNovoResultado,
        juizCabine: { nome: operador.nome, funcao: operador.funcao },
        juizBoiTv: boiTvJuizNome || "Juiz BOI TV",
      });

      setBoiTvNumeroSenha("");
      setDisputa(data?.disputa || null);
    } catch (e) {
      alert(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }

  async function classificarFaixa() {
    if (!podeJulgar) return alert("Somente o JUIZ pode classificar faixa.");
    setLoading(true);
    try {
      const { data } = await apiPainel.post("/disputa/classificar-faixa", {
        eventoId,
        categoria,
        rodada: Number(faixaRodada),
        de: Number(faixaDe),
        ate: Number(faixaAte),
        operador: { nome: operador.nome, funcao: operador.funcao },
      });

      setDisputa(data?.disputa || null);
    } catch (e) {
      alert(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }

  async function encerrarDisputa() {
    if (!podeJulgar) return alert("Somente o JUIZ pode encerrar.");
    setLoading(true);
    try {
      const { data } = await apiPainel.post("/disputa/encerrar", {
        eventoId,
        categoria,
        operador: { nome: operador.nome, funcao: operador.funcao },
      });

      setDisputa(data?.disputa || null);
    } catch (e) {
      alert(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }

  const rodadaAtualObj = useMemo(() => {
    const num = disputa?.rodadaAtual;
    return disputa?.rodadas?.find((r) => r.numero === num) || null;
  }, [disputa]);

  const atual = rodadaAtualObj?.fila?.[0] || null;

  return (
    <div style={{ padding: 16, maxWidth: 1200, margin: "0 auto", color: "white" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, color: "#fdd212" }}>🏆 Disputa (Tempo Real)</h2>
          <div style={{ opacity: 0.85, marginTop: 6 }}>
            Operador: <b>{operador?.nome}</b> ({operador?.funcao}) | Categoria:{" "}
            <b style={{ color: "#fdd212" }}>{categoria}</b>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => navigate("/painel/rodizio")} style={btnDark}>
            📺 Rodízio
          </button>
          <button onClick={() => navigate("/painel")} style={btnDark}>
            ⬅️ Painel
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 14, marginTop: 16 }}>
        {/* CARD PRINCIPAL */}
        <div style={cardBig}>
          {!disputa ? (
            <div>
              <p style={{ marginTop: 0, opacity: 0.85 }}>
                Ainda não existe disputa aberta pra esse evento/categoria.
              </p>

              {podeJulgar ? (
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <div>
                    <div style={lbl}>Qtd Prêmios</div>
                    <input
                      type="number"
                      value={qtdPremios}
                      onChange={(e) => setQtdPremios(e.target.value)}
                      style={inp}
                    />
                  </div>

                  <button style={btnPrimary} onClick={iniciarDisputa} disabled={loading}>
                    {loading ? "Iniciando..." : "Iniciar Disputa ✅"}
                  </button>
                </div>
              ) : (
                <div style={{ opacity: 0.75 }}>
                  ⚠️ Apenas o <b>JUIZ</b> pode iniciar a disputa.
                </div>
              )}
            </div>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontWeight: 900, fontSize: 18, color: "#fdd212" }}>
                  Rodada Atual: {disputa.rodadaAtual}
                </div>

                <div style={{ opacity: 0.9 }}>
                  Status:{" "}
                  <b style={{ color: disputa.status === "ENCERRADA" ? "#ff6b6b" : "#22c55e" }}>
                    {disputa.status}
                  </b>
                </div>
              </div>

              <div style={{ marginTop: 10, opacity: 0.9 }}>
                Fila: <b style={{ color: "#fdd212" }}>{rodadaAtualObj?.fila?.length || 0}</b> | Perdidas:{" "}
                <b style={{ color: "#ff6b6b" }}>{rodadaAtualObj?.perdidas?.length || 0}</b> | Válidas:{" "}
                <b style={{ color: "#22c55e" }}>{rodadaAtualObj?.validas?.length || 0}</b>
              </div>

              {!atual ? (
                <div style={{ marginTop: 18, opacity: 0.75 }}>
                  ✅ Não tem senha na fila da rodada atual.
                </div>
              ) : (
                <div style={{ marginTop: 14 }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <div style={badgeSenha}>#{atual.numeroSenha}</div>
                    <div style={{ opacity: 0.9 }}>
                      <b style={{ color: "#fdd212" }}>{atual.nomeVaqueiro || "-"}</b>
                    </div>
                  </div>

                  <div style={{ marginTop: 10 }}>
                    <div style={line}>
                      <b style={k}>Esteira:</b> {atual.nomeEsteira || "-"}
                    </div>
                    <div style={line}>
                      <b style={k}>Cavalo (puxar):</b> {atual.cavaloPuxar || "-"}
                    </div>
                    <div style={line}>
                      <b style={k}>Cavalo (esteira):</b> {atual.cavaloEsteira || "-"}
                    </div>
                    <div style={line}>
                      <b style={k}>Representação:</b> {atual.representacao || "-"}
                    </div>
                    <div style={line}>
                      <b style={k}>Cidade:</b> {atual.cidade || "-"}
                    </div>

                    <div style={line}>
                      <b style={k}>BOI TV:</b>{" "}
                      <span style={{ color: atual.boiTv ? "#22c55e" : "#ff6b6b", fontWeight: 800 }}>
                        {atual.boiTv ? "SIM ✅" : "NÃO"}
                      </span>
                    </div>
                  </div>

                  {/* BOTÕES DO JUIZ */}
                  <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                    {podeJulgar ? (
                      <>
                        <button style={btnSuccess} onClick={() => acaoDisputa("VALIDO")} disabled={loading}>
                          ✅ Válido
                        </button>

                        <button style={btnDanger} onClick={() => acaoDisputa("ZERO")} disabled={loading}>
                          ❌ Zero
                        </button>

                        <button style={btnSecondary} onClick={() => acaoDisputa("RETORNO")} disabled={loading}>
                          🔁 Retorno
                        </button>
                      </>
                    ) : (
                      <div style={{ opacity: 0.75 }}>
                        ⚠️ Apenas o <b>JUIZ</b> pode julgar.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Rodadas */}
              <div style={{ marginTop: 18 }}>
                <div style={{ fontWeight: 900, color: "#fdd212", marginBottom: 8 }}>🎯 Rodadas</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {(disputa.rodadas || []).map((r) => (
                    <button
                      key={r.numero}
                      style={{
                        ...btnSecondary,
                        borderColor: r.numero === disputa.rodadaAtual ? "#fdd212" : "rgba(255,255,255,.1)",
                      }}
                      onClick={() => mudarRodada(r.numero)}
                      disabled={loading || !podeJulgar}
                    >
                      Rodada {r.numero}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* LATERAL */}
        <div style={cardSide}>
          <h3 style={{ marginTop: 0, color: "#fdd212" }}>⚙️ Controles</h3>

          {!disputa ? (
            <div style={{ opacity: 0.8 }}>Inicie a disputa para liberar os controles.</div>
          ) : (
            <>
              {/* BOI TV */}
              <div style={{ marginBottom: 14 }}>
                <h4 style={{ margin: "10px 0", color: "#fdd212" }}>📺 Revisão BOI TV</h4>

                <div style={grid2}>
                  <div>
                    <div style={lbl}>Rodada</div>
                    <input
                      type="number"
                      value={boiTvRodada}
                      onChange={(e) => setBoiTvRodada(e.target.value)}
                      style={inp}
                    />
                  </div>

                  <div>
                    <div style={lbl}>Senha</div>
                    <input
                      value={boiTvNumeroSenha}
                      onChange={(e) => setBoiTvNumeroSenha(e.target.value)}
                      style={inp}
                      placeholder="Ex: 216"
                    />
                  </div>
                </div>

                <div style={{ marginTop: 10 }}>
                  <div style={lbl}>Novo Resultado</div>
                  <select
                    value={boiTvNovoResultado}
                    onChange={(e) => setBoiTvNovoResultado(e.target.value)}
                    style={inp}
                  >
                    <option value="VALIDO">VALIDO</option>
                    <option value="RETORNO">RETORNO</option>
                    <option value="ZERO">ZERO</option>
                  </select>
                </div>

                <div style={{ marginTop: 10 }}>
                  <div style={lbl}>Juiz BOI TV</div>
                  <input
                    value={boiTvJuizNome}
                    onChange={(e) => setBoiTvJuizNome(e.target.value)}
                    style={inp}
                    placeholder="Ex: Juiz TV Paulo"
                  />
                </div>

                <button style={btnPrimary} onClick={revisaoBoiTv} disabled={loading || !podeJulgar}>
                  Aplicar BOI TV ✅
                </button>
              </div>

              {/* CLASSIFICAR FAIXA */}
              <div style={{ marginBottom: 14 }}>
                <h4 style={{ margin: "10px 0", color: "#fdd212" }}>🏅 Classificar Faixa</h4>

                <div style={grid3}>
                  <div>
                    <div style={lbl}>Rodada</div>
                    <input
                      type="number"
                      value={faixaRodada}
                      onChange={(e) => setFaixaRodada(e.target.value)}
                      style={inp}
                    />
                  </div>

                  <div>
                    <div style={lbl}>De</div>
                    <input
                      type="number"
                      value={faixaDe}
                      onChange={(e) => setFaixaDe(e.target.value)}
                      style={inp}
                    />
                  </div>

                  <div>
                    <div style={lbl}>Até</div>
                    <input
                      type="number"
                      value={faixaAte}
                      onChange={(e) => setFaixaAte(e.target.value)}
                      style={inp}
                    />
                  </div>
                </div>

                <button style={btnPrimary} onClick={classificarFaixa} disabled={loading || !podeJulgar}>
                  Classificar Faixa ✅
                </button>
              </div>

              {/* ENCERRAR */}
              <div>
                <h4 style={{ margin: "10px 0", color: "#fdd212" }}>🚨 Encerrar</h4>
                <button style={btnDanger} onClick={encerrarDisputa} disabled={loading || !podeJulgar}>
                  Encerrar Disputa ❌
                </button>
              </div>
            </>
          )}

          {/* GANHADORES */}
          <div style={{ marginTop: 18 }}>
            <h4 style={{ margin: "10px 0", color: "#fdd212" }}>🏆 Ganhadores</h4>

            {!disputa?.ganhadores?.length ? (
              <div style={{ opacity: 0.8 }}>Ainda não tem ganhadores.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {disputa.ganhadores.map((g) => (
                  <div key={g._id || `${g.posicao}-${g.numeroSenha}`} style={miniCard}>
                    <b style={{ color: "#fdd212" }}>{g.posicao}º</b> — #{g.numeroSenha} — {g.nomeVaqueiro}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 14, opacity: 0.7, fontSize: 13 }}>
        * Somente o <b>Juiz</b> pode julgar e encerrar a disputa.
      </div>
    </div>
  );
}

const cardBig = {
  background: "linear-gradient(145deg, #1e1e1e, #2a2a2a)",
  color: "#fff",
  borderRadius: 16,
  padding: 16,
  boxShadow: "0 10px 25px rgba(0,0,0,.25)",
  border: "1px solid rgba(255,255,255,.05)",
  minHeight: 250,
};

const cardSide = {
  background: "linear-gradient(145deg, #1e1e1e, #2a2a2a)",
  borderRadius: 16,
  padding: 16,
  boxShadow: "0 10px 25px rgba(0,0,0,.25)",
  border: "1px solid rgba(255,255,255,.05)",
  color: "white",
};

const badgeSenha = {
  padding: "8px 12px",
  borderRadius: 14,
  background: "#fdd212",
  color: "#111",
  fontWeight: 900,
  fontSize: 18,
};

const k = { color: "#fdd212" };
const line = { marginTop: 6, opacity: 0.95 };

const lbl = { fontSize: 13, opacity: 0.9, marginBottom: 6 };

const inp = {
  width: "100%",
  padding: 10,
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,.1)",
  outline: "none",
  background: "rgba(255,255,255,.08)",
  color: "#fff",
};

const btnDark = {
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,.1)",
  background: "#1e1e1e",
  color: "#fff",
  cursor: "pointer",
  fontWeight: 800,
};

const btnPrimary = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 12,
  border: "none",
  background: "#fdd212",
  color: "#111",
  cursor: "pointer",
  fontWeight: 900,
  marginTop: 10,
};

const btnSecondary = {
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,.1)",
  background: "#242424",
  color: "#fff",
  cursor: "pointer",
  fontWeight: 800,
};

const btnSuccess = {
  padding: "10px 12px",
  borderRadius: 12,
  border: "none",
  background: "#4caf50",
  color: "#fff",
  cursor: "pointer",
  fontWeight: 900,
};

const btnDanger = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 12,
  border: "none",
  background: "#f44336",
  color: "#fff",
  cursor: "pointer",
  fontWeight: 900,
};

const miniCard = {
  padding: 10,
  borderRadius: 14,
  border: "1px solid rgba(255,255,255,.05)",
  background: "rgba(255,255,255,.05)",
};

const grid2 = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 8,
};

const grid3 = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  gap: 8,
};
